import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { User, Phone, MapPin, Lock, RefreshCw, Check, ChevronDown, ChevronUp, XCircle } from 'lucide-react';
import { customerAPI, authAPI } from '../../services/api';

export default function CustomerProfile() {
  const [customer, setCustomer]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [section, setSection]     = useState(null); // 'profile' | 'password' | 'plan'
  const [saving, setSaving]       = useState(false);

  const [profileForm, setProfile] = useState({ name: '', alternateMobile: '', address: { line1: '', area: '', city: '' } });
  const [passForm, setPass]       = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [planForm, setPlan]       = useState({ requestedPlan: '', notes: '' });
  const [discReason, setDiscReason] = useState('');

  useEffect(() => {
    customerAPI.getMe().then(r => {
      const c = r.data.data;
      setCustomer(c);
      setProfile({
        name: c.name || '',
        alternateMobile: c.alternateMobile || '',
        address: { line1: c.address?.line1 || '', area: c.address?.area || '', city: c.address?.city || '' },
      });
    }).catch(() => toast.error('Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (s) => setSection(prev => prev === s ? null : s);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await customerAPI.updateMe(profileForm);
      setCustomer(prev => ({ ...prev, ...profileForm }));
      toast.success('Profile updated');
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirm) { toast.error('New passwords do not match'); return; }
    if (passForm.newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setSaving(true);
    try {
      await authAPI.changePassword({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      toast.success('Password changed successfully');
      setPass({ currentPassword: '', newPassword: '', confirm: '' });
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password change failed');
    } finally { setSaving(false); }
  };

  const submitDisconnect = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to request disconnection? Our team will contact you to confirm.')) return;
    setSaving(true);
    try {
      const res = await customerAPI.requestDisconnection({ reason: discReason });
      toast.success(`Disconnection request sent — Ticket: ${res.data.data.ticketNumber}`);
      setDiscReason('');
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Request failed');
    } finally { setSaving(false); }
  };

  const submitPlanChange = async (e) => {
    e.preventDefault();
    if (!planForm.requestedPlan.trim()) { toast.error('Please describe the plan you want'); return; }
    setSaving(true);
    try {
      const res = await customerAPI.requestPlanChange(planForm);
      toast.success(`Plan change request submitted — Ticket: ${res.data.data.ticketNumber}`);
      setPlan({ requestedPlan: '', notes: '' });
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Request failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading...</div>;
  if (!customer) return null;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 20, fontSize: '1.15rem' }}>My Account</h2>

      {/* Info card */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '1.2rem' }}>
            {customer.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{customer.name}</div>
            <div style={{ color: '#888', fontSize: '.82rem' }}>ID: {customer.customerId}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '.88rem', color: '#444' }}>
          <span><Phone size={13} style={ico} />{customer.mobile}</span>
          {customer.alternateMobile && <span><Phone size={13} style={ico} />{customer.alternateMobile} (alternate)</span>}
          <span><MapPin size={13} style={ico} />
            {[customer.address?.line1, customer.address?.area, customer.address?.city].filter(Boolean).join(', ') || '—'}
          </span>
          {customer.catv?.package && (
            <span style={{ color: '#4f46e5', fontWeight: 600 }}>
              Plan: {customer.catv.package.displayName} — ₹{customer.catv.package.price}/mo
            </span>
          )}
          {customer.catv?.expiryDate && (
            <span>Expiry: {fmtDate(customer.catv.expiryDate)}</span>
          )}
          {customer.catv?.setTopBoxSerial && (
            <span style={{ color: '#888', fontSize: '.8rem' }}>STB: {customer.catv.setTopBoxSerial}</span>
          )}
        </div>
      </div>

      {/* Edit Profile */}
      <div style={{ ...card, marginTop: 14 }}>
        <button onClick={() => toggle('profile')} style={sectionBtn}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><User size={16} /> Edit Profile</span>
          {section === 'profile' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {section === 'profile' && (
          <form onSubmit={saveProfile} style={{ marginTop: 14 }}>
            <Field label="Full Name" value={profileForm.name} onChange={v => setProfile(f => ({ ...f, name: v }))} />
            <Field label="Alternate Mobile" value={profileForm.alternateMobile} onChange={v => setProfile(f => ({ ...f, alternateMobile: v }))} />
            <Field label="Address" value={profileForm.address.line1} onChange={v => setProfile(f => ({ ...f, address: { ...f.address, line1: v } }))} />
            <Field label="Area" value={profileForm.address.area} onChange={v => setProfile(f => ({ ...f, address: { ...f.address, area: v } }))} />
            <Field label="City" value={profileForm.address.city} onChange={v => setProfile(f => ({ ...f, address: { ...f.address, city: v } }))} />
            <button type="submit" disabled={saving} style={submitBtn}><Check size={15} /> {saving ? 'Saving...' : 'Save Changes'}</button>
          </form>
        )}
      </div>

      {/* Change Password */}
      <div style={{ ...card, marginTop: 14 }}>
        <button onClick={() => toggle('password')} style={sectionBtn}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Lock size={16} /> Change Password</span>
          {section === 'password' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {section === 'password' && (
          <form onSubmit={savePassword} style={{ marginTop: 14 }}>
            <Field label="Current Password" value={passForm.currentPassword} onChange={v => setPass(f => ({ ...f, currentPassword: v }))} type="password" hint="Default: last 4 digits of your STB serial number" />
            <Field label="New Password" value={passForm.newPassword} onChange={v => setPass(f => ({ ...f, newPassword: v }))} type="password" />
            <Field label="Confirm New Password" value={passForm.confirm} onChange={v => setPass(f => ({ ...f, confirm: v }))} type="password" />
            <button type="submit" disabled={saving} style={submitBtn}><Lock size={15} /> {saving ? 'Updating...' : 'Change Password'}</button>
          </form>
        )}
      </div>

      {/* Plan Change Request */}
      <div style={{ ...card, marginTop: 14 }}>
        <button onClick={() => toggle('plan')} style={sectionBtn}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><RefreshCw size={16} /> Request Plan Change</span>
          {section === 'plan' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {section === 'plan' && (
          <form onSubmit={submitPlanChange} style={{ marginTop: 14 }}>
            <div style={{ padding: '8px 12px', background: '#f0f9ff', borderRadius: 8, fontSize: '.83rem', color: '#0369a1', marginBottom: 12 }}>
              Our team will contact you within 24 hours to process your plan change.
            </div>
            <Field label="Which plan do you want?" value={planForm.requestedPlan} onChange={v => setPlan(f => ({ ...f, requestedPlan: v }))} placeholder="e.g. Power Plus, Royal HD, etc." />
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: '.85rem', color: '#555', display: 'block', marginBottom: 4 }}>Additional Notes (optional)</label>
              <textarea value={planForm.notes} onChange={e => setPlan(f => ({ ...f, notes: e.target.value }))}
                rows={3} placeholder="Any specific requirements..."
                style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <button type="submit" disabled={saving} style={submitBtn}><RefreshCw size={15} /> {saving ? 'Submitting...' : 'Submit Request'}</button>
          </form>
        )}
      </div>

      {/* Disconnection Request */}
      <div style={{ ...card, marginTop: 14, borderColor: '#fca5a5' }}>
        <button onClick={() => toggle('disconnect')} style={{ ...sectionBtn, color: '#dc2626' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><XCircle size={16} /> Request Disconnection</span>
          {section === 'disconnect' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {section === 'disconnect' && (
          <form onSubmit={submitDisconnect} style={{ marginTop: 14 }}>
            <div style={{ padding: '8px 12px', background: '#fff1f2', borderRadius: 8, fontSize: '.83rem', color: '#9f1239', marginBottom: 12 }}>
              Submitting this request will notify our team. Your service will be disconnected after confirmation.
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '.85rem', color: '#555', display: 'block', marginBottom: 4 }}>Reason for disconnection (optional)</label>
              <textarea value={discReason} onChange={e => setDiscReason(e.target.value)}
                rows={3} placeholder="e.g. Moving to another city, switching provider, etc."
                style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #fca5a5', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <button type="submit" disabled={saving}
              style={{ ...submitBtn, background: '#dc2626' }}>
              <XCircle size={15} /> {saving ? 'Sending...' : 'Submit Disconnection Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, hint }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: '.85rem', color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box' }} />
      {hint && <p style={{ fontSize: '.75rem', color: '#888', margin: '3px 0 0' }}>{hint}</p>}
    </div>
  );
}

const card      = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 8px rgba(0,0,0,.08)' };
const sectionBtn = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '.95rem', padding: 0 };
const submitBtn  = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '11px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', marginTop: 4 };
const ico        = { marginRight: 6, verticalAlign: 'middle' };
